import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { NumberInput } from "../../shared/NumberInput";
import { Slider } from "../../shared/Slider";
import { fmtEUR } from "../../lib/calculations";
import { TMI_OPTIONS, type SimulationInput } from "../../lib/types";
import { AffiliateCard } from "@/components/AffiliateCard";

interface Props {
  input: SimulationInput;
  update: <K extends keyof SimulationInput>(k: K, v: SimulationInput[K]) => void;
  mensualite: number;
  coutInterets: number;
}

export function StepFinancement({ input, update, mensualite, coutInterets }: Props) {
  const [advOpen, setAdvOpen] = useState(false);
  // Auto-suggest montant emprunté = prix - apport quand on coche le crédit ou modifie l'apport
  useEffect(() => {
    if (input.hasCredit && input.montantEmprunte === 0) {
      const suggested = Math.max(0, input.prixAcquisition - input.apport);
      update("montantEmprunte", suggested);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.hasCredit]);

  return (
    <div className="space-y-5">
      <header>
        <p className="text-sm font-medium text-primary">Étape 4 / 4</p>
        <h2 className="mt-1 text-2xl font-bold text-foreground">Comment financez-vous ?</h2>
      </header>

      <NumberInput
        label="Apport personnel"
        value={input.apport}
        onValueChange={(n) => update("apport", n)}
        required
        placeholder="ex: 50 000"
      />

      <div className="space-y-2">
        <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/50 p-4">
          <div>
            <p className="text-sm font-medium text-foreground">Avez-vous un crédit ?</p>
            <p className="text-xs text-muted-foreground">Active les calculs d'effet de levier</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={input.hasCredit}
            aria-label="Avez-vous un crédit ?"
            onClick={() => update("hasCredit", !input.hasCredit)}
            className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 ${
              input.hasCredit
                ? "border-primary bg-primary"
                : "border-border bg-muted"
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-1 ring-black/5 transition-transform ${
                input.hasCredit ? "translate-x-6" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>
        {!input.hasCredit && (
          <p className="px-1 text-xs text-muted-foreground">
            9 investisseurs LMNP sur 10 ont un crédit
          </p>
        )}
      </div>

      {input.hasCredit && (
        <div className="space-y-4 overflow-hidden animate-slide-down">
          <NumberInput
            label="Montant emprunté"
            value={input.montantEmprunte}
            onValueChange={(n) => update("montantEmprunte", n)}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Slider
              label="Taux annuel"
              value={input.tauxAnnuel}
              onChange={(n) => update("tauxAnnuel", n)}
              min={0.5}
              max={7}
              step={0.05}
              suffix="%"
              display={`${input.tauxAnnuel.toFixed(2)}%`}
            />
            <Slider
              label="Durée"
              value={input.dureeAnnees}
              onChange={(n) => update("dureeAnnees", n)}
              min={5}
              max={30}
              step={1}
              suffix=" ans"
              display={`${input.dureeAnnees} ans`}
            />
          </div>

          {/* Indicateur taux marché */}
          {(() => {
            const t = input.tauxAnnuel;
            const tone =
              t > 4
                ? "border-danger/30 bg-danger/5 text-danger"
                : t < 3.5
                ? "border-success/30 bg-success/5 text-success"
                : "border-border bg-muted/40 text-muted-foreground";
            const msg =
              t > 4
                ? "Votre taux semble élevé"
                : t < 3.5
                ? "Excellent taux !"
                : "Taux dans la moyenne";
            return (
              <div
                className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-xs ${tone}`}
                role="status"
              >
                <span>Taux moyen actuel : 3,75 - 3,95%</span>
                <strong className="font-semibold">{msg}</strong>
              </div>
            );
          })()}

          {/* Card affiliation crédit (Placement 1) */}
          <AffiliateCard variant="credit" placement="step4-form" />

          <Slider
            label="Assurance emprunteur"
            value={input.assuranceEmprunteurPct}
            onChange={(n) => update("assuranceEmprunteurPct", n)}
            min={0.1}
            max={1}
            step={0.05}
            suffix="%"
            display={`${input.assuranceEmprunteurPct.toFixed(2)}%`}
            helper="En % du capital emprunté"
          />

          {/* Card affiliation assurance (Placement 2) — si taux > 0.25% */}
          {input.assuranceEmprunteurPct > 0.25 && input.montantEmprunte > 0 && (
            <div className="space-y-2">
              {(() => {
                const economies = Math.max(
                  0,
                  ((input.assuranceEmprunteurPct - 0.1) / 100) *
                    input.montantEmprunte *
                    input.dureeAnnees
                );
                if (economies < 3000) return null;
                return (
                  <p className="text-xs font-medium text-success">
                    Vous pourriez économiser jusqu'à{" "}
                    <strong>{fmtEUR(economies)}</strong> sur la durée du prêt
                  </p>
                );
              })()}
              <AffiliateCard
                variant="assurance"
                placement="step4-assurance"
                compact
              />
            </div>
          )}

          <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-1" role="status">
            <p className="text-sm text-foreground">
              Mensualité crédit :{" "}
              <strong className="tabular font-bold">{fmtEUR(mensualite)}</strong>
            </p>
            <p className="text-sm text-muted-foreground">
              Coût total des intérêts :{" "}
              <strong className="tabular text-foreground">{fmtEUR(coutInterets)}</strong>
            </p>
          </div>
        </div>
      )}

      {/* Mode avancé */}
      <div className="rounded-xl border border-border bg-secondary/30">
        <button
          type="button"
          onClick={() => setAdvOpen((v) => !v)}
          aria-expanded={advOpen}
          className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium text-foreground hover:bg-muted/50 rounded-xl transition"
        >
          <span>Mode avancé — fiscalité &amp; inflation</span>
          <ChevronDown
            className={`h-4 w-4 transition-transform ${advOpen ? "rotate-180" : ""}`}
          />
        </button>
        {advOpen && (
          <div className="space-y-5 border-t border-border px-4 py-5 animate-fade-in-up">
            <div>
              <p className="mb-2 text-sm font-medium text-foreground">
                Tranche marginale d'imposition (TMI)
              </p>
              <p className="mb-3 text-xs text-muted-foreground">
                Votre tranche d'imposition sur le revenu. Influence l'impôt LMNP estimé.
              </p>
              <div className="grid grid-cols-4 gap-2">
                {TMI_OPTIONS.map((t) => {
                  const active = Math.abs(input.tmi - t) < 0.001;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => update("tmi", t)}
                      className={`rounded-lg border px-3 py-2 text-sm font-semibold tabular transition ${
                        active
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-background text-foreground hover:border-primary/50"
                      }`}
                    >
                      {Math.round(t * 100)}%
                    </button>
                  );
                })}
              </div>
            </div>

            <Slider
              label="Inflation des loyers"
              value={input.inflationLoyers}
              onChange={(n) => update("inflationLoyers", n)}
              min={0}
              max={5}
              step={0.1}
              suffix="%/an"
              display={`${input.inflationLoyers.toFixed(1)} %/an`}
              helper="Revalorisation annuelle moyenne (IRL ~1.5%)"
            />
            <Slider
              label="Inflation des charges"
              value={input.inflationCharges}
              onChange={(n) => update("inflationCharges", n)}
              min={0}
              max={6}
              step={0.1}
              suffix="%/an"
              display={`${input.inflationCharges.toFixed(1)} %/an`}
              helper="Hausse moyenne taxe foncière, copro, entretien"
            />
            <Slider
              label="Valorisation du bien"
              value={input.inflationBien}
              onChange={(n) => update("inflationBien", n)}
              min={0}
              max={5}
              step={0.1}
              suffix="%/an"
              display={`${input.inflationBien.toFixed(1)} %/an`}
              helper="Hypothèse d'évolution du prix de l'immobilier"
            />
          </div>
        )}
      </div>
    </div>
  );
}
